<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { BRUSH_WIDTH, parseColor, PEN_WIDTH_MAX, PEN_WIDTH_MIN, type BrushKind, type StrokeMode, type ToolDef } from '@vp/render';
import { live, ui } from '../editor/bridge';
import { pasteFromData } from '../editor/clipboard';
import { template } from '../editor/diagram';
import { t } from '../i18n';
import { settings } from '../platform';
import Icon from './Icon.vue';
import { insertModuleTemplate, moduleTemplates } from '../modules';

/**
 * Left tool pill (§6.2): frequent tools stay visible, related variants open in flyouts,
 * everything else (frame, image, module tools) lives in the "More" menu.
 */
interface Group {
  id: string;
  label: string;
  tools: string[];
}

const GROUPS: Group[] = [
  { id: 'select', label: 'tools.select', tools: ['select', 'lasso'] },
  { id: 'hand', label: 'tools.hand', tools: ['hand'] },
  { id: 'shapes', label: 'tools.shapes', tools: ['rect', 'pill', 'ellipse', 'triangle', 'diamond', 'manualInput', 'data', 'document', 'display', 'bracket', 'brace', 'braceLeft'] },
  { id: 'text', label: 'tools.text', tools: ['text'] },
  { id: 'sticker', label: 'tools.sticker', tools: ['sticker'] },
  { id: 'lines', label: 'tools.lines', tools: ['arrow', 'line', 'connector'] },
  { id: 'path', label: 'tools.path', tools: ['path'] },
  { id: 'pen', label: 'tools.pen', tools: ['pen'] },
  { id: 'eraser', label: 'tools.eraser', tools: ['eraser'] },
];

const PEN_COLORS = ['#1f2937', '#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ffffff'];
const BRUSH_LIST: { id: BrushKind; icon: string }[] = [
  { id: 'ink', icon: 'brushInk' },
  { id: 'highlighter', icon: 'brushHighlighter' },
];
const STROKE_MODES: { id: StrokeMode; icon: string; label: string }[] = [
  { id: 'sharp', icon: 'strokeSharp', label: 'tools.penSharp' },
  { id: 'smooth', icon: 'strokeSmooth', label: 'tools.penSmooth' },
];

const lastInGroup = ref<Record<string, string>>({ select: 'select', shapes: 'rect', lines: 'arrow' });
const flyout = ref<string | null>(null);
const moreOpen = ref(false);
/** Which chip of the pen/path bar is open. The bar itself is the properties-style pill. */
const toolPop = ref<null | 'brush' | 'stroke' | 'color' | 'width'>(null);

function closePops(): void {
  moreOpen.value = false;
  flyout.value = null;
  toolPop.value = null;
}

function onEsc(e: KeyboardEvent): void {
  if (e.key === 'Escape' && (moreOpen.value || flyout.value || toolPop.value)) {
    closePops();
    e.stopPropagation();
  }
}
watch([moreOpen, flyout, toolPop], ([m, f, p]) => {
  window.removeEventListener('keydown', onEsc, true);
  if (m || f || p) window.addEventListener('keydown', onEsc, true);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onEsc, true);
  penObs?.disconnect();
});
watch(
  () => ui.tool,
  () => {
    toolPop.value = null;
  },
);
watch(
  () => ui.zen,
  (on) => {
    if (on) closePops();
  },
);

const moduleTools = computed<ToolDef[]>(() => {
  void ui.toolsRev;
  return live.editor?.toolList.filter((x) => x.moduleId) ?? [];
});

interface Extra {
  id: string;
  label: string;
  icon?: string;
  svg?: string;
  action?: () => void;
}

const extras = computed<Extra[]>(() => [
  { id: 'node', label: t('tools.node'), icon: 'node' },
  { id: 'frame', label: t('tools.frame'), icon: 'frame' },
  { id: 'image', label: t('tools.image'), icon: 'image', action: insertImage },
  { id: 'diagram', label: t('diagram.tool'), icon: 'diagram', action: () => (ui.diagramEdit = { id: null, source: template('flow') }) },
  ...moduleTools.value.map((m) => ({ id: m.id, label: m.label ?? m.id, svg: m.icon, icon: m.icon ? undefined : 'module' })),
]);

const activeExtra = computed(() => extras.value.find((x) => x.id === ui.tool) ?? null);
const penStyle = computed(() => settings.pen);
const pathStyle = computed(() => settings.pathPen);
const penHsl = computed(() => hexToHsl(penStyle.value.color));
const pathHsl = computed(() => hexToHsl(pathStyle.value.color));

function pick(group: Group, tool?: string): void {
  let id = tool ?? lastInGroup.value[group.id] ?? group.tools[0];
  if (!group.tools.includes(id)) id = group.tools[0];
  if (group.tools.length > 1) lastInGroup.value[group.id] = id;
  live.editor?.setTool(id);
  flyout.value = null;
  moreOpen.value = false;
}

function pickExtra(x: Extra): void {
  moreOpen.value = false;
  if (x.action) x.action();
  else live.editor?.setTool(x.id);
}

function groupActive(g: Group): boolean {
  return g.tools.includes(ui.tool);
}

function groupIcon(g: Group): string {
  return groupActive(g) ? ui.tool : lastInGroup.value[g.id] ?? g.tools[0];
}

function toolLabel(id: string): string {
  return t('tools.' + id);
}

function setPen(patch: Partial<typeof settings.pen>): void {
  settings.pen = { ...settings.pen, ...patch };
}

function setPath(patch: Partial<typeof settings.pathPen>): void {
  settings.pathPen = { ...settings.pathPen, ...patch };
}

function setBrush(b: BrushKind): void {
  setPen({ brush: b, width: BRUSH_WIDTH[b] });
}

/** The rainbow ring of the custom swatch; filled with the color once a custom one is chosen. */
function customStyle(color: string): Record<string, string> {
  if (PEN_COLORS.includes(color.toLowerCase())) return {};
  return { background: `radial-gradient(circle, ${color} 0 42%, transparent 46%), conic-gradient(red, yellow, lime, cyan, blue, magenta, red)` };
}

function setMode(mode: StrokeMode): void {
  setPen({ mode });
}

const strokeTool = computed(() => (ui.tool === 'pen' || ui.tool === 'path' ? ui.tool : null));
const strokeStyle = computed(() => (ui.tool === 'pen' ? penStyle.value : pathStyle.value));
const strokeHsl = computed(() => (ui.tool === 'pen' ? penHsl.value : pathHsl.value));

function toggleToolPop(id: 'brush' | 'stroke' | 'color' | 'width'): void {
  toolPop.value = toolPop.value === id ? null : id;
}

function setStroke(patch: { color?: string; width?: number }): void {
  if (ui.tool === 'pen') setPen(patch);
  else setPath(patch);
}

function setStrokeHsl(part: 'h' | 's' | 'l', v: number): void {
  if (ui.tool === 'pen') setPenHsl(part, v);
  else setPathHsl(part, v);
}

function widthLabel(w: number): string {
  return String(Math.round(w * 10) / 10);
}

/** The tool pill sits in the top row, the same place as the properties bar. Under that bar when a selection has one. */
const penWrap = ref<HTMLElement | null>(null);
const penPlace = reactive({ left: 12, right: 12, top: 12 });
const penWrapStyle = computed(() => ({ left: penPlace.left + 'px', right: penPlace.right + 'px', top: penPlace.top + 'px' }));
let penObs: ResizeObserver | null = null;

function rowTop(stageWidth: number, crowded: boolean): number {
  if (stageWidth <= 760) return 108;
  if (crowded || stageWidth <= 1360) return 64;
  return 12;
}

function placePen(): void {
  const el = penWrap.value;
  const stage = el?.parentElement;
  if (!el || !stage) return;
  const sr = stage.getBoundingClientRect();
  const tl = stage.querySelector('.top-left')?.getBoundingClientRect();
  const tr = stage.querySelector('.top-right')?.getBoundingClientRect();
  const gap = 10;
  const left = tl ? tl.right - sr.left + gap : 12;
  const right = tr ? sr.right - tr.left + gap : 12;
  const props = stage.querySelector('.propbar-wrap')?.getBoundingClientRect();
  const propsShown = !!props && props.width > 0 && props.height > 0;
  const room = sr.width - left - right;
  const bar = el.querySelector('.pen-pop') as HTMLElement | null;
  const crowded = (bar?.scrollWidth ?? 0) + 2 > room;
  penPlace.left = crowded ? 12 : left;
  penPlace.right = crowded ? 12 : right;
  penPlace.top = propsShown && props ? props.bottom - sr.top + 8 : rowTop(sr.width, crowded);
}

watch(penWrap, (el) => {
  penObs?.disconnect();
  penObs = null;
  if (!el) return;
  const stage = el.parentElement;
  penObs = new ResizeObserver(() => requestAnimationFrame(placePen));
  for (const x of [stage, stage?.querySelector('.top-left'), stage?.querySelector('.top-right'), el.querySelector('.pen-pop')]) if (x) penObs.observe(x);
  requestAnimationFrame(placePen);
});
watch(
  () => ui.selection.length + (ui.barBelow ? 1 : 0) + (ui.editingText ? 1 : 0),
  () => void nextTick(() => requestAnimationFrame(placePen)),
);

function setPenHsl(part: 'h' | 's' | 'l', v: number): void {
  const hsl = hexToHsl(settings.pen.color);
  hsl[part] = v;
  setPen({ color: hslToHex(hsl.h, hsl.s, hsl.l) });
}

function setPathHsl(part: 'h' | 's' | 'l', v: number): void {
  const hsl = hexToHsl(settings.pathPen.color);
  hsl[part] = v;
  setPath({ color: hslToHex(hsl.h, hsl.s, hsl.l) });
}

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const [r, g, b] = parseColor(hex);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s, l };
}

function hslToHex(h: number, s: number, l: number): string {
  const hue = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] = hue < 60 ? [c, x, 0] : hue < 120 ? [x, c, 0] : hue < 180 ? [0, c, x] : hue < 240 ? [0, x, c] : hue < 300 ? [x, 0, c] : [c, 0, x];
  const to = (v: number) => Math.round((v + m) * 255)
    .toString(16)
    .padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

function svgUrl(svg: string): string {
  // module markup is never injected into the DOM: rendered as an isolated image
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

function insertImage(): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.onchange = async () => {
    const f = input.files?.[0];
    if (!f) return;
    const dt = new DataTransfer();
    dt.items.add(f);
    await pasteFromData(dt);
  };
  input.click();
}
</script>

<template>
  <div class="float pillbar vertical toolbar" :class="{ bare: ui.zen }" role="toolbar" aria-orientation="vertical" :aria-label="t('tools.title')">
    <div v-for="g in GROUPS" :key="g.id" class="group">
      <button
        class="icon-btn tool"
        :class="{ active: groupActive(g) }"
        :disabled="ui.readOnly && g.id !== 'select' && g.id !== 'hand'"
        :aria-label="g.tools.length > 1 ? toolLabel(lastInGroup[g.id] ?? g.tools[0]) : t(g.label)"
        :aria-pressed="groupActive(g)"
        :title="g.tools.length > 1 ? toolLabel(lastInGroup[g.id] ?? g.tools[0]) : t(g.label)"
        @click="pick(g)"
        @contextmenu.prevent="g.tools.length > 1 && (flyout = flyout === g.id ? null : g.id)"
      >
        <Icon :name="groupIcon(g)" :size="20" />
      </button>
      <button
        v-if="g.tools.length > 1"
        class="more-tri"
        :aria-label="t(g.label)"
        :aria-expanded="flyout === g.id"
        :disabled="ui.readOnly"
        @click="flyout = flyout === g.id ? null : g.id"
      ></button>
      <div v-if="flyout === g.id" class="flyout" role="menu">
        <button v-for="id in g.tools" :key="id" class="icon-btn" :class="{ active: ui.tool === id }" role="menuitem" :title="toolLabel(id)" :aria-label="toolLabel(id)" @click="pick(g, id)">
          <Icon :name="id" :size="20" />
        </button>
      </div>
    </div>
    <span class="sep" role="separator"></span>
    <div class="group">
      <button
        class="icon-btn tool"
        :class="{ active: !!activeExtra || moreOpen }"
        :disabled="ui.readOnly"
        :aria-label="activeExtra ? activeExtra.label : t('tools.more')"
        :title="activeExtra ? activeExtra.label : t('tools.more')"
        :aria-expanded="moreOpen"
        @click="moreOpen = !moreOpen"
      >
        <img v-if="activeExtra?.svg" class="mod-icon" :src="svgUrl(activeExtra.svg)" alt="" />
        <Icon v-else :name="activeExtra?.icon ?? 'apps'" :size="20" />
      </button>
      <div v-if="moreOpen" class="float card more-menu" role="menu" :aria-label="t('tools.more')">
        <p class="group-title">{{ t('tools.more') }}</p>
        <button v-for="x in extras" :key="x.id" class="extra" :class="{ active: ui.tool === x.id }" role="menuitem" @click="pickExtra(x)">
          <span class="ico">
            <img v-if="x.svg" class="mod-icon" :src="svgUrl(x.svg)" alt="" />
            <Icon v-else :name="x.icon ?? 'module'" :size="18" />
          </span>
          <span>{{ x.label }}</span>
        </button>
        <!-- API 2.2: templates of modules -->
        <template v-if="moduleTemplates().length && !ui.readOnly">
          <p class="group-title tpl-title">{{ t('tools.templates') }}</p>
          <button
            v-for="tp in moduleTemplates()"
            :key="tp.moduleId + tp.id"
            class="extra"
            role="menuitem"
            :title="tp.hint"
            :data-template="tp.moduleId + ':' + tp.id"
            @click="(moreOpen = false), insertModuleTemplate(tp.moduleId, tp.id)"
          >
            <span class="ico"><Icon name="frame" :size="18" /></span>
            <span>{{ tp.label }}</span>
          </button>
        </template>
      </div>
    </div>
  </div>
  <!-- same top row as the properties bar; the card opens under the chip -->
  <div v-if="strokeTool && !ui.zen" ref="penWrap" class="pen-wrap" :style="penWrapStyle" @pointerdown.stop @keydown.stop>
    <div class="pen-pop float pillbar" role="toolbar" :aria-label="strokeTool === 'pen' ? t('tools.pen') : t('tools.path')">
      <template v-if="strokeTool === 'pen'">
        <button type="button" class="chip" :class="{ on: toolPop === 'brush' }" :aria-expanded="toolPop === 'brush'" :title="t('tools.brushTitle')" @click="toggleToolPop('brush')">
          <Icon :name="BRUSH_LIST.find((b) => b.id === penStyle.brush)?.icon ?? 'brushInk'" :size="15" />
          <span>{{ t('tools.brush.' + penStyle.brush) }}</span>
        </button>
        <button type="button" class="chip" :class="{ on: toolPop === 'stroke' }" :aria-expanded="toolPop === 'stroke'" :title="t('tools.strokeTitle')" @click="toggleToolPop('stroke')">
          <Icon :name="STROKE_MODES.find((m) => m.id === penStyle.mode)?.icon ?? 'strokeSmooth'" :size="15" />
          <span>{{ t(STROKE_MODES.find((m) => m.id === penStyle.mode)?.label ?? 'tools.penSmooth') }}</span>
        </button>
        <span class="sep" role="separator"></span>
      </template>
      <button type="button" class="chip" :class="{ on: toolPop === 'color' }" :aria-expanded="toolPop === 'color'" :aria-label="t('tools.penColor')" :title="t('tools.penColor')" @click="toggleToolPop('color')">
        <span class="swatch-btn" :style="{ background: strokeStyle.color }"></span>
      </button>
      <button type="button" class="chip" :class="{ on: toolPop === 'width' }" :aria-expanded="toolPop === 'width'" :aria-label="t('tools.penWidth')" :title="t('tools.penWidth')" @click="toggleToolPop('width')">
        <span class="num">{{ widthLabel(strokeStyle.width) }}</span>
      </button>
    </div>
    <div
      v-if="toolPop"
      class="pen-drop float card"
      role="dialog"
      :aria-label="toolPop === 'brush' ? t('tools.brushTitle') : toolPop === 'stroke' ? t('tools.strokeTitle') : toolPop === 'width' ? t('tools.penWidth') : t('tools.penColor')"
    >
      <div v-if="toolPop === 'brush'" class="seg-pills" role="radiogroup" :aria-label="t('tools.brushTitle')">
        <button
          v-for="b in BRUSH_LIST"
          :key="b.id"
          type="button"
          role="radio"
          :class="{ active: penStyle.brush === b.id }"
          :aria-checked="penStyle.brush === b.id"
          :data-brush="b.id"
          @click="setBrush(b.id)"
        >
          <Icon :name="b.icon" :size="14" /><span>{{ t('tools.brush.' + b.id) }}</span>
        </button>
      </div>
      <div v-else-if="toolPop === 'stroke'" class="seg-pills" role="radiogroup" :aria-label="t('tools.strokeTitle')">
        <button
          v-for="m in STROKE_MODES"
          :key="m.id"
          type="button"
          role="radio"
          :class="{ active: penStyle.mode === m.id }"
          :aria-checked="penStyle.mode === m.id"
          :data-mode="m.id"
          @click="setMode(m.id)"
        >
          <Icon :name="m.icon" :size="14" /><span>{{ t(m.label) }}</span>
        </button>
      </div>
      <template v-else-if="toolPop === 'color'">
        <div class="swatches">
          <button
            v-for="c in PEN_COLORS"
            :key="c"
            type="button"
            class="swatch-btn"
            :class="{ active: strokeStyle.color.toLowerCase() === c }"
            :style="{ background: c }"
            :aria-label="t('tools.penColor') + ' ' + c"
            @click="setStroke({ color: c })"
          ></button>
          <label class="swatch-btn custom" :class="{ active: !PEN_COLORS.includes(strokeStyle.color.toLowerCase()) }" :style="customStyle(strokeStyle.color)" :title="t('tools.penCustom')">
            <input type="color" :value="strokeStyle.color.slice(0, 7)" :aria-label="t('tools.penCustom')" @input="setStroke({ color: ($event.target as HTMLInputElement).value })" />
          </label>
        </div>
        <label class="slide"><span>{{ t('tools.penHue') }}</span><input type="range" min="0" max="360" step="1" :value="strokeHsl.h" :aria-label="t('tools.penHue')" @input="setStrokeHsl('h', +($event.target as HTMLInputElement).value)" /></label>
        <label class="slide"><span>{{ t('tools.penSat') }}</span><input type="range" min="0" max="1" step="0.01" :value="strokeHsl.s" :aria-label="t('tools.penSat')" @input="setStrokeHsl('s', +($event.target as HTMLInputElement).value)" /></label>
        <label class="slide"><span>{{ t('tools.penLight') }}</span><input type="range" min="0" max="1" step="0.01" :value="strokeHsl.l" :aria-label="t('tools.penLight')" @input="setStrokeHsl('l', +($event.target as HTMLInputElement).value)" /></label>
      </template>
      <label v-else class="slide">
        <span>{{ t('tools.penWidth') }}</span>
        <input type="range" :min="PEN_WIDTH_MIN" :max="PEN_WIDTH_MAX" step="1" :value="strokeStyle.width" :aria-label="t('tools.penWidth')" @input="setStroke({ width: +($event.target as HTMLInputElement).value })" />
        <span class="num">{{ widthLabel(strokeStyle.width) }}</span>
      </label>
    </div>
  </div>
  <p v-if="!ui.zen && ui.pathEditing" class="pen-hint" role="status">{{ t('tools.pathEditHint') }}</p>
  <p v-else-if="!ui.zen && ui.tool === 'path'" class="pen-hint" role="status">{{ t('tools.pathHint') }}</p>
  <div v-if="moreOpen || flyout || toolPop" class="tool-backdrop" @pointerdown="closePops"></div>
</template>

<style scoped>
.toolbar {
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 21;
}
.toolbar.bare.float {
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
  backdrop-filter: none;
  padding: 0;
  gap: 4px;
}
.toolbar.bare .sep {
  display: none;
}
.toolbar.bare .tool.active {
  border-radius: var(--radius-control);
}
.toolbar.bare .flyout,
.toolbar.bare .more-menu {
  left: 40px;
}
.group {
  position: relative;
}
.tool {
  width: 36px;
  height: 36px;
}
/* the selected tool's fill follows the panel corner, not the round button */
.tool.active,
.flyout .icon-btn.active {
  border-radius: var(--radius-panel);
}
.more-tri {
  position: absolute;
  right: 3px;
  bottom: 3px;
  width: 7px;
  height: 7px;
  padding: 0;
  border: none;
  background: linear-gradient(135deg, transparent 50%, #94a3b8 50%);
  cursor: pointer;
}
.flyout {
  position: absolute;
  left: 46px;
  top: 0;
  display: flex;
  gap: 2px;
  padding: 4px;
  background: var(--panel);
  border: 1px solid var(--float-border);
  border-radius: var(--radius-panel);
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.12);
  z-index: 50;
}
.pen-wrap {
  position: absolute;
  z-index: 24;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: max-content;
  max-width: calc(100% - 24px);
  margin: 0 auto;
  gap: 8px;
  /* the bar is centered in the row; the empty sides must not catch presses */
  pointer-events: none;
}
.pen-wrap > * {
  pointer-events: auto;
}
.pen-pop,
.pen-drop {
  position: static;
}
.pen-drop {
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: max-content;
  max-width: min(280px, calc(100vw - 80px));
}
.swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.custom {
  background: conic-gradient(red, yellow, lime, cyan, blue, magenta, red);
  overflow: hidden;
}
.custom input {
  opacity: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  cursor: pointer;
}
.slide {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text-2);
}
.slide span {
  width: 92px;
  flex: none;
}
.slide input {
  flex: 1;
  min-width: 80px;
}
.slide .num {
  width: auto;
  text-align: right;
}
.pen-hint {
  position: absolute;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: 20;
  margin: 0;
  width: max-content;
  max-width: min(560px, calc(100% - 320px));
  text-align: center;
  padding: 6px 10px;
  border-radius: var(--radius-panel);
  background: rgba(15, 23, 42, 0.82);
  color: #fff;
  font-size: 11.5px;
  line-height: 1.4;
  pointer-events: none;
}
.flyout .icon-btn {
  border-radius: var(--radius-control);
}
.width {
  width: 28px;
  height: 28px;
}
.dot {
  border-radius: 50%;
  display: block;
}
.more-menu {
  position: absolute;
  left: 46px;
  bottom: -6px;
  width: 230px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px;
  z-index: 50;
}
.more-menu .group-title {
  margin: 2px 8px 6px;
}
.more-menu .tpl-title {
  margin-top: 10px;
}
.extra {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 36px;
  padding: 0 10px;
  border: none;
  background: none;
  border-radius: var(--radius-panel);
  cursor: pointer;
  color: var(--text-2);
  text-align: left;
}
.extra:hover {
  background: var(--hover);
  color: var(--text);
}
.extra.active {
  background: var(--accent-soft);
  color: var(--accent-text);
}
.ico {
  width: 22px;
  display: inline-flex;
  justify-content: center;
}
.mod-icon {
  display: block;
  width: 20px;
  height: 20px;
}
.tool-backdrop {
  position: fixed;
  inset: 0;
  z-index: 19;
}
</style>
